import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const PipelineStatus = ({ currentPath, pipelineId }) => {
    const [pipelineStatus, setPipelineStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoRefreshing, setAutoRefreshing] = useState(false);

    const fetchPipelineStatus = async () => {
        if (!pipelineId) return;
        
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(getApiUrl(`/api/pipeline/status/${pipelineId}`));
            setPipelineStatus(response.data);
        } catch (err) {
            setError('Failed to fetch pipeline status');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (pipelineId) {
            fetchPipelineStatus();
            // Poll for status updates every 10 seconds if pipeline is running
            const interval = setInterval(() => {
                if (pipelineStatus && (pipelineStatus.status === 'running' || pipelineStatus.status === 'RUNNING')) {
                    console.log('Auto-refreshing pipeline status...');
                    setAutoRefreshing(true);
                    fetchPipelineStatus().finally(() => {
                        setTimeout(() => setAutoRefreshing(false), 1000); // Show indicator for 1 second
                    });
                }
            }, 20000); // Poll every 20 seconds (increased from 10)
            
            return () => clearInterval(interval);
        }
    }, [pipelineId, pipelineStatus?.status]); // Re-run when status changes

    const getStatusColor = (status) => {
        switch (status) {
            case 'running':
                return 'text-warning';
            case 'success':
                return 'text-success';
            case 'failed':
                return 'text-danger';
            case 'stopped':
                return 'text-warning';
            case 'cancelled':
                return 'text-secondary';
            default:
                return 'text-muted';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'running':
                return 'bi-arrow-clockwise';
            case 'success':
                return 'bi-check-circle-fill';
            case 'failed':
                return 'bi-x-circle-fill';
            case 'stopped':
                return 'bi-pause-circle-fill';
            case 'cancelled':
                return 'bi-x-circle';
            default:
                return 'bi-question-circle';
        }
    };

    const formatDuration = (startedAt, completedAt) => {
        if (!startedAt) return 'N/A';
        
        const start = new Date(startedAt);
        const end = completedAt ? new Date(completedAt) : new Date();
        const duration = Math.floor((end - start) / 1000);
        
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        
        return `${minutes}m ${seconds}s`;
    };

    if (!pipelineId) return null;

    if (loading) {
        return (
            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span>Loading pipeline status...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card shadow-sm mb-3 border-danger">
                <div className="card-body">
                    <div className="d-flex align-items-center text-danger">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span>{error}</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!pipelineStatus) return null;

    return (
        <div className="card shadow-sm mb-3">
            <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Pipeline Status
                        {autoRefreshing && (
                            <span className="ms-2 badge bg-info">
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Auto-refreshing
                            </span>
                        )}
                    </h6>
                    <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={fetchPipelineStatus}
                        disabled={loading || autoRefreshing}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </button>
                </div>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <strong>Status:</strong>
                            <span className={`ms-2 ${getStatusColor(pipelineStatus.status)}`}>
                                <i className={`bi ${getStatusIcon(pipelineStatus.status)} me-1`}></i>
                                {pipelineStatus.status.charAt(0).toUpperCase() + pipelineStatus.status.slice(1)}
                            </span>
                        </div>
                        
                        {pipelineStatus.octoVersion && (
                            <div className="mb-2">
                                <strong>Octo Version:</strong>
                                <span className="ms-2 text-primary">{pipelineStatus.octoVersion}</span>
                            </div>
                        )}
                        
                        {pipelineStatus.infraVersion && (
                            <div className="mb-2">
                                <strong>Infra Version:</strong>
                                <span className="ms-2 text-primary">{pipelineStatus.infraVersion}</span>
                            </div>
                        )}
                        
                        <div className="mb-2">
                            <strong>Duration:</strong>
                            <span className="ms-2">{formatDuration(pipelineStatus.startedAt, pipelineStatus.completedAt)}</span>
                        </div>
                    </div>
                    
                    <div className="col-md-6">
                        <div className="mb-2">
                            <strong>Started:</strong>
                            <span className="ms-2">
                                {new Date(pipelineStatus.startedAt).toLocaleString()}
                            </span>
                        </div>
                        
                        {pipelineStatus.completedAt && (
                            <div className="mb-2">
                                <strong>Completed:</strong>
                                <span className="ms-2">
                                    {new Date(pipelineStatus.completedAt).toLocaleString()}
                                </span>
                            </div>
                        )}
                        
                        {pipelineStatus.errorMessage && (
                            <div className="mb-2">
                                <strong>Error:</strong>
                                <span className="ms-2 text-danger">{pipelineStatus.errorMessage}</span>
                            </div>
                        )}
                    </div>
                </div>
                
                {pipelineStatus.pipelineUrl && (
                    <div className="mt-3">
                        <a 
                            href={pipelineStatus.pipelineUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm"
                        >
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            View Pipeline Details
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PipelineStatus; 